import { Injectable, inject } from "@angular/core";
import { ApiResource } from "@auvious/common";
import { AuviousRtcService } from "./rtc.service";
import { GenericErrorHandler } from "./error-handlers.service";
import {
  IWidgetConfiguration,
  IWidgetConfigurationRequest,
  IWidgetConfigurationSummary,
  IWidgetDeployment,
  IWidgetDeploymentRequest,
} from "@auvious/integrations";
import { IPagedResponse, PageableRequestBuilder } from "../models";

@Injectable()
export class WidgetService {
  private errorHandler = inject(GenericErrorHandler);

  private widgetConfigResource: ApiResource;
  private widgetDeploymentResource: ApiResource;

  constructor() {
    const rtcService = inject(AuviousRtcService);

    rtcService.common$.subscribe((common) => {
      this.widgetConfigResource = common.apiResourceFactory(
        "api/widget/configuration"
      );
      this.widgetDeploymentResource = common.apiResourceFactory(
        "api/widget/deployment"
      );
    });
  }

  /** widget config */

  public getWidgetConfigurationsSummary(): Promise<
    IWidgetConfigurationSummary[]
  > {
    const query = new PageableRequestBuilder()
      .pageSize(200)
      .pageNumber(0)
      .sortOrder("desc")
      .sortField("modifiedAt")
      .build();

    return this.widgetConfigResource
      .get({
        params: query.getParams(),
        urlPostfix: "/summary",
      })
      .then((response) => response.entities)
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Only clients with ROLE_ADMIN authority are allowed.
   * @returns IPagedResponse<IWidgetConfiguration>
   */
  public getWidgetConfigurations(): Promise<IWidgetConfiguration[]> {
    const query = new PageableRequestBuilder()
      .pageSize(200)
      .pageNumber(0)
      .sortOrder("asc")
      .sortField("version")
      .build();
    return this.widgetConfigResource
      .get({
        params: query.getParams(),
        urlPostfix: "/",
      })
      .then((response) => response.entities)
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Only clients with ROLE_ADMIN authority are allowed.
   * @param config IWidgetConfigurationRequest
   * @returns IWidgetConfiguration
   */
  public createWidgetConfiguration(
    config: IWidgetConfigurationRequest
  ): Promise<IWidgetConfiguration> {
    return this.widgetConfigResource.create(config).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  /**
   * Version is used for optimistic locking. Always supply the latest known version.
   *  Only clients with ROLE_ADMIN authority are allowed.
   * @param config IWidgetConfiguration
   * @returns If wrong version is used, then a 409 Conflict HTTP response status will be returned.
   */
  public updateWidgetConfiguration(
    config: IWidgetConfiguration
  ): Promise<IWidgetConfiguration> {
    return this.widgetConfigResource.update(config).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  /**
   * Only clients with ROLE_ADMIN authority are allowed.
   * @param id widget configuration id
   * @returns If an entity is found it is deleted and 204 HTTP status will be returned.
   * @returns If an entity with the given id is doesn't exist, 204 HTTP status will be returned.
   */
  public removeWidgetConfiguration(id: string): Promise<void> {
    return this.widgetConfigResource.deleteById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  /**
   * Only clients with ROLE_ADMIN authority are allowed.
   * @param id widget configuration id
   * @returns IWidgetConfiguration
   */
  public getWidgetConfiguration(id: string): Promise<IWidgetConfiguration> {
    return this.widgetConfigResource.getById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  /**
   * Only clients with ROLE_ADMIN authority are allowed.
   * @param id
   * @param version
   * @returns If an entity is found it is deleted and 204 HTTP status will be returned.
   * @returns If an entity with the given id and version doesn't exist, 204 HTTP status will be returned.
   */
  public removeWidgetConfigurationVersion(
    id: string,
    version: number
  ): Promise<void> {
    return this.widgetConfigResource
      .delete({
        urlPostfix: [id, "version", version].join("/"),
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getWidgetConfigurationVersion(id: string, version: number) {
    return this.widgetConfigResource
      .get({
        urlPostfix: [id, "version", version].join("/"),
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getWidgetConfigurationVersions(
    id: string
  ): Promise<IWidgetConfiguration[]> {
    return this.widgetConfigResource
      .get({
        urlPostfix: [id, "versions"].join("/"),
      })
      .then((response) => response.entities)
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getWidgetDeployments(): Promise<IWidgetDeployment[]> {
    const query = new PageableRequestBuilder()
      .pageSize(200)
      .pageNumber(0)
      .sortOrder('desc')
      .sortField('createdAt')
      .build();
    return this.widgetDeploymentResource
      .get({ params: query.getParams() })
      .then((response) => response.entities)
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public createWidgetDeployment(
    entity: IWidgetDeploymentRequest
  ): Promise<IWidgetDeployment> {
    return this.widgetDeploymentResource.create(entity).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public updateWidgetDeployment(
    entity: IWidgetDeployment
  ): Promise<IWidgetDeployment> {
    return this.widgetDeploymentResource.update(entity).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public removeWidgetDeployment(id: string): Promise<void> {
    return this.widgetDeploymentResource.deleteById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public geWidgetDeployment(id: string): Promise<IWidgetDeployment> {
    return this.widgetDeploymentResource.getById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }
}
